import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BookOpen,
  Boxes,
  CheckSquare,
  FileText,
  FolderKanban,
  ListChecks,
  Loader2,
  MessageSquare,
  Search,
  X,
} from 'lucide-react'
import { api } from '../lib/api'
import { searchResponse } from '../lib/types'
import { useAuthStore } from '../stores/authStore'

const TYPE_ICON = {
  task: CheckSquare,
  list: ListChecks,
  folder: FolderKanban,
  space: Boxes,
  comment: MessageSquare,
  sop: BookOpen,
  doc: FileText,
}

const TYPE_LABEL = {
  task: 'Tasks',
  list: 'Lists',
  folder: 'Projects',
  space: 'Spaces',
  comment: 'Comments',
  sop: 'SOPs',
  doc: 'Documents',
}

// Single source of truth for group order — the flat navigation list and the
// rendered groups MUST iterate types in the SAME order, or the arrow-key
// cursor maps to the wrong row.
const TYPE_ORDER = ['task', 'list', 'folder', 'space', 'sop', 'doc', 'comment']

/**
 * Global Cmd/Ctrl+K palette. Mounted once at the App root; listens for
 * the keyboard shortcut and renders a centred dialog with grouped
 * search results. Debounces queries to avoid hammering the backend on
 * every keystroke. Permission-filtering already happens server-side,
 * so we just render whatever comes back.
 */
export function SearchPalette() {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  // Role drives which routes the palette navigates to — admin and
  // user panels mount different layouts and have different deep-link
  // targets for the same record. Admin has no per-task or per-doc
  // deep view, so those fall back to the closest list/landing page.
  const role = useAuthStore((s) => s.user?.role)
  const isAdmin = role === 'company_admin'
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef(null)
  const navigate = useNavigate()

  // Global Cmd/Ctrl+K listener.
  useEffect(() => {
    function onKey(e) {
      const isCmdK = (e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')
      if (isCmdK) {
        e.preventDefault()
        setOpen((cur) => !cur)
      } else if (e.key === 'Escape' && open) {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  // Reset state every time it opens.
  useEffect(() => {
    if (open) {
      setQuery('')
      setDebounced('')
      setActiveIndex(0)
      // Focus on next paint so the input is mounted.
      setTimeout(() => inputRef.current?.focus(), 50)
    }
  }, [open])

  // Debounce query so the backend isn't hit on every keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 220)
    return () => clearTimeout(id)
  }, [query])

  const searchQuery = useQuery({
    queryKey: ['search', debounced],
    queryFn: async () => {
      const res = await api.get('/search', { params: { q: debounced, limit: 8 } })
      return searchResponse.parse(res.data)
    },
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  })

  // Flatten results into a single navigable list so arrow keys work.
  const flat = useMemo(() => {
    const out = []
    const r = searchQuery.data?.results
    if (!r) return out
    for (const type of TYPE_ORDER) {
      for (const hit of r[type] ?? []) out.push({ ...hit, type })
    }
    return out
  }, [searchQuery.data])

  // Reset selection whenever results change.
  useEffect(() => {
    setActiveIndex(0)
  }, [flat])

  function navigateToHit(hit) {
    setOpen(false)
    // Route choice is role-aware. Previously the palette navigated to
    // routes that don't exist (`/app/spaces/_/lists/:id` with a literal
    // `_` because the search response doesn't include spaceId, and
    // `/app/tasks/:id` which isn't mounted). The board route
    // (`/app/board/:listId`) only needs `listId` so works directly.
    switch (hit.type) {
      case 'task': {
        const listId = hit.meta?.listId
        if (!listId) break
        // Admin task monitor is `/tasks` (no per-task deep link); user
        // gets the kanban board view of the task's list.
        navigate(isAdmin ? '/tasks' : `/app/board/${listId}`)
        break
      }
      case 'comment': {
        const listId = hit.meta?.listId
        if (!listId) break
        // Comments live inside tasks. Same destination as the task hit —
        // the list view shows the task with its comments.
        navigate(isAdmin ? '/tasks' : `/app/board/${listId}`)
        break
      }
      case 'sop':
        // SOPs page exists on both panels at different paths.
        navigate(isAdmin ? '/sops' : '/app/sops')
        break
      case 'doc': {
        // Document deep view is only mounted under the user panel.
        // For admins, fall back to the Files page since there's no
        // per-document admin view yet.
        navigate(isAdmin ? '/files' : `/app/documents/${hit.id}`)
        break
      }
      case 'space': {
        // Space deep view is under the user panel. Admins have no per-Space
        // route, so land on the Workspaces overview.
        navigate(isAdmin ? '/workspaces' : `/app/spaces/${hit.id}`)
        break
      }
      case 'list': {
        // Open the List inside its Space when we know the Space; otherwise
        // the board route (which only needs the listId). Admins fall back to
        // the Task Monitor.
        const spaceId = hit.meta?.spaceId
        navigate(
          isAdmin
            ? '/tasks'
            : spaceId
              ? `/app/spaces/${spaceId}/lists/${hit.id}`
              : `/app/board/${hit.id}`,
        )
        break
      }
      case 'folder': {
        // A Project (Folder) lives inside a Space; deep-link to that Space.
        const spaceId = hit.meta?.spaceId
        if (!spaceId) break
        navigate(isAdmin ? '/workspaces' : `/app/spaces/${spaceId}`)
        break
      }
      default:
        break
    }
  }

  function onInputKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIndex((i) => Math.min(flat.length - 1, i + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIndex((i) => Math.max(0, i - 1))
    } else if (e.key === 'Enter' && flat[activeIndex]) {
      e.preventDefault()
      navigateToHit(flat[activeIndex])
    }
  }

  if (!open) return null

  const counts = searchQuery.data?.counts
  const showTooShort = debounced.length > 0 && debounced.length < 2

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/40 flex items-start justify-center pt-[12vh] px-4"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-2xl rounded-[14px] bg-[color:var(--color-card)] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[color:var(--color-rule)]">
          <Search size={18} className="text-[#0a2349] shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder="Search Tasks, Lists, Projects, Spaces, SOPs, Comments, Documents…"
            className="flex-1 bg-transparent outline-none text-base text-[color:var(--color-ink-strong)] placeholder:text-[color:var(--color-ink-muted)]"
          />
          {searchQuery.isFetching && (
            <Loader2
              size={16}
              className="animate-spin text-[color:var(--color-ink-muted)]"
            />
          )}
          <button
            onClick={() => setOpen(false)}
            className="text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {showTooShort ? (
            <EmptyMessage>Type at least 2 characters to search.</EmptyMessage>
          ) : debounced.length === 0 ? (
            <EmptyMessage hint>
              <kbd className="font-mono text-xs px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)]">
                ↑
              </kbd>{' '}
              <kbd className="font-mono text-xs px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)]">
                ↓
              </kbd>{' '}
              to navigate ·{' '}
              <kbd className="font-mono text-xs px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)]">
                Enter
              </kbd>{' '}
              to open ·{' '}
              <kbd className="font-mono text-xs px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)]">
                Esc
              </kbd>{' '}
              to close
            </EmptyMessage>
          ) : flat.length === 0 && !searchQuery.isFetching ? (
            <EmptyMessage>No matches for "{debounced}".</EmptyMessage>
          ) : (
            <ResultsGroups
              results={searchQuery.data?.results}
              counts={counts}
              flatList={flat}
              activeIndex={activeIndex}
              onHover={(idx) => setActiveIndex(idx)}
              onPick={navigateToHit}
            />
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] text-[12px] text-[color:var(--color-ink-muted)] flex items-center justify-between">
          <span>
            {counts
              ? (() => {
                  const total = Object.values(counts).reduce(
                    (a, b) => a + (b ?? 0),
                    0,
                  )
                  return `${total} result${total === 1 ? '' : 's'}`
                })()
              : 'Powered by Mongo $text search'}
          </span>
          <span>
            <kbd className="font-mono px-1 py-0.5 rounded bg-[color:var(--color-card)] border border-[color:var(--color-rule)]">
              ⌘ K
            </kbd>{' '}
            to toggle
          </span>
        </div>
      </div>
    </div>
  )
}

function EmptyMessage({ children, hint }) {
  return (
    <div
      className={`px-6 py-10 text-center text-sm ${
        hint
          ? 'text-[color:var(--color-ink-muted)]'
          : 'text-[color:var(--color-ink-muted)]'
      }`}
    >
      {children}
    </div>
  )
}

function ResultsGroups({ results, counts, activeIndex, onHover, onPick }) {
  // Precompute each non-empty group's flat starting offset so the per-row
  // index matches the flattened navigation list WITHOUT mutating a counter
  // during render (react-hooks/immutability). Order MUST equal `flat`'s.
  const groups = []
  let offset = 0
  for (const type of TYPE_ORDER) {
    const hits = results?.[type] ?? []
    if (hits.length === 0) continue
    groups.push({ type, hits, start: offset })
    offset += hits.length
  }
  return (
    <div className="py-1">
      {groups.map(({ type, hits, start }) => {
        const Icon = TYPE_ICON[type]
        return (
          <div key={type}>
            <div className="px-4 pt-3 pb-1 text-xs uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)]">
              {TYPE_LABEL[type]} · {counts?.[type] ?? hits.length}
            </div>
            {hits.map((hit, i) => {
              const idx = start + i
              const active = activeIndex === idx
              return (
                <button
                  key={`${type}-${hit.id}`}
                  type="button"
                  onMouseEnter={() => onHover(idx)}
                  onClick={() => onPick({ ...hit, type })}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                    active
                      ? 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-ink-strong)]'
                      : 'text-[color:var(--color-ink)] hover:bg-[color:var(--color-card-hover)]'
                  }`}
                >
                  <Icon
                    size={16}
                    className={`shrink-0 ${
                      active
                        ? 'text-[color:var(--color-accent)]'
                        : 'text-[color:var(--color-ink-muted)]'
                    }`}
                  />
                  <span className="flex-1 truncate text-sm">{hit.title}</span>
                  <span className="text-xs uppercase tracking-wider text-[color:var(--color-ink-muted)] shrink-0">
                    {type}
                  </span>
                </button>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
